import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "prosemirror-state"
import { Decoration, DecorationSet } from "prosemirror-view"

import type { Node as ProseMirrorNode } from "prosemirror-model"

export type AiSelectionHighlightRange = {
  from: number
  to: number
}

export const aiSelectionHighlightKey = new PluginKey<AiSelectionHighlightRange | null>(
  "aiSelectionHighlight"
)

function normalizeRange(
  range: AiSelectionHighlightRange,
  doc: ProseMirrorNode
): AiSelectionHighlightRange | null {
  const maxPos = doc.content.size
  const from = Math.max(0, Math.min(range.from, maxPos))
  const to = Math.max(0, Math.min(range.to, maxPos))

  if (from === to) return null
  return from < to ? { from, to } : { from: to, to: from }
}

export const AiSelectionHighlight = Extension.create({
  name: "aiSelectionHighlight",
  addProseMirrorPlugins() {
    return [
      new Plugin<AiSelectionHighlightRange | null>({
        key: aiSelectionHighlightKey,
        state: {
          init: () => null,
          apply: (tr, value) => {
            const meta = tr.getMeta(aiSelectionHighlightKey) as
              | AiSelectionHighlightRange
              | null
              | undefined
            if (meta === null) return null
            if (meta) return normalizeRange(meta, tr.doc)

            if (!value || !tr.docChanged) return value

            const mappedRange = {
              from: tr.mapping.map(value.from),
              to: tr.mapping.map(value.to),
            }
            return normalizeRange(mappedRange, tr.doc)
          },
        },
        props: {
          decorations: (state) => {
            const range = aiSelectionHighlightKey.getState(state)
            if (!range) return null

            return DecorationSet.create(state.doc, [
              Decoration.inline(range.from, range.to, { class: "ai-selection-highlight" }),
            ])
          },
        },
      }),
    ]
  },
})
